import React, { useState } from 'react';

import { BillingActions } from '@Actions/BillingActions';
import { OrderActions } from '@Actions/OrderActions';
import LottieSuccessGreen from '@Components/animations/success-green';
import BottomPopup from '@Components/core/bottom-popup';
import Button from '@Components/design-system/button-v2';
import { TextPalette } from '@Components/design-system/color-v2/types';
import Icon from '@Components/design-system/icon';
import { NotificationType, showNotification } from '@Components/design-system/notification/notification';
import TextInput from '@Components/design-system/text-input';
import Text from '@Components/design-system/text-v2';
import { FontType } from '@Components/design-system/text-v2/types';
import ConditionCard from '@Components/scheduling/condition-card';
import { useAppDispatch } from '@Redux/hooks';
import { callHelpline } from '@Utils/hooks/call-helpline';

export interface GetHelpModalProps {
  onClose: () => void;
  userId: string;
  consultationId: string;
}

const refundReasons = ["Doctor didn't join the call", 'Poor audio / video quality', 'Booked by mistake', 'Other'];

const GetHelpModal: React.FC<GetHelpModalProps> = ({ onClose, userId, consultationId }) => {
  const dispatch = useAppDispatch();
  const [step, setStep] = useState<'options' | 'refund' | 'success'>('options');
  const [selectedReason, setSelectedReason] = useState<string>('');
  const [otherReason, setOtherReason] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleTalkToTeam = () => {
    callHelpline(userId);
    onClose();
  };

  const handleRefundSubmit = async () => {
    const reasonToSubmit = selectedReason === 'Other' ? otherReason.trim() : selectedReason;
    if (!reasonToSubmit) return;
    try {
      setIsSubmitting(true);
      const isSuccess = await dispatch(
        BillingActions.RefundConsultation({
          reason: reasonToSubmit,
          patientId: userId,
          consultationId,
        }),
      );
      if (isSuccess) {
        setStep('success');
        dispatch(OrderActions.GetConsultationOrders({ patientId: userId }));
      } else {
        showNotification(NotificationType.ERROR, 'Failed to submit refund request. Please try again.');
      }
    } catch {
      showNotification(NotificationType.ERROR, 'Failed to submit refund request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <BottomPopup handleCloseFrame={onClose} width='400px'>
      {step === 'options' ? (
        <div className='p-6 flex flex-col gap-6'>
          <Text font={FontType.H4_B} color={TextPalette.DARK_1} className='text-center'>
            How can we help you?
          </Text>
          <Button
            variant='primary'
            size='large'
            fullWidth
            text='Talk to our team'
            onClick={handleTalkToTeam}
            view='icon-text'
          />
          <Button
            variant='tertiary'
            size='large'
            fullWidth
            text='Get a refund'
            onClick={() => setStep('refund')}
            view='text'
          />
        </div>
      ) : step === 'refund' ? (
        <div className='p-6 flex flex-col gap-6 justify-between'>
          <div className='flex items-center gap-2 mb-2'>
            <Icon name='arrow_back' size='s' onClick={() => setStep('options')} />
            <Text font={FontType.H4_B} color={TextPalette.DARK_1}>
              Select refund reason
            </Text>
          </div>
          <div className='flex flex-col gap-4 mb-6'>
            {refundReasons.map((reason) => (
              <ConditionCard
                key={reason}
                language={reason}
                isSelected={selectedReason === reason}
                onClick={() => {
                  setSelectedReason(reason);
                  if (reason !== 'Other') setOtherReason('');
                }}
              />
            ))}
            {selectedReason === 'Other' && (
              <TextInput placeholderText='Please specify your reason' value={otherReason} onChange={setOtherReason} />
            )}
          </div>
          <Button
            variant='primary'
            size='large'
            fullWidth
            text='Submit Request'
            onClick={handleRefundSubmit}
            disabled={!selectedReason || (selectedReason === 'Other' && !otherReason.trim()) || isSubmitting}
            view='icon-text'
          />
        </div>
      ) : (
        <div className='p-6 flex flex-col items-center gap-6 py-12'>
          <LottieSuccessGreen />
          <Text font={FontType.H4_B} color={TextPalette.DARK_1} className='text-center'>
            Refund request submitted
          </Text>
          <Text font={FontType.BODY_L} color={TextPalette.DARK_2} className='text-center'>
            Your refund will be processed to your original payment method, typically within 5-7 business days.
          </Text>
          <Button variant='primary' size='large' fullWidth text='Close' onClick={onClose} view='icon-text' />
        </div>
      )}
    </BottomPopup>
  );
};

export default GetHelpModal;
